import { SyntheticEvent } from 'react'
import { RouteObject } from 'react-router'

import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { ListSubheader, Tooltip, Zoom } from '@mui/material'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { COLLAPSE_ANIMATION } from '@/constants/navigationDrawer'

import { RichTooltip } from '../RichTooltip'
import { NavMenuItem } from './NavMenuItem'
import { ListContainer } from './NavMenuListContainer'

type NavMenuItemContentProps = {
  item: RouteObject
  baseStyle?: boolean
  disableTooltip?: boolean
  children?: RouteObject[]
  expanded: boolean
  toggleExpanded: (event: SyntheticEvent) => void
  isSelected: () => boolean
  hover: boolean
  isNavMenuExpanded: boolean
  iconless?: boolean
  allChildrenWithoutIcons?: boolean
}

const ICONLESS_EXPANDED_WIDTH = 36
const ICONLESS_COLLAPSED_WIDTH = 22

export const NavMenuItemContent = ({
  item,
  baseStyle,
  disableTooltip,
  children,
  expanded,
  toggleExpanded,
  isSelected,
  hover,
  isNavMenuExpanded,
  iconless,
  allChildrenWithoutIcons
}: NavMenuItemContentProps) => (
  <RichTooltip
    placement="right-start"
    distance={1}
    paperSx={{ borderRadius: 4, padding: 0 }}
    open={!!(!isNavMenuExpanded && children && hover && !disableTooltip)}
    content={
      <ListContainer
        sx={{
          paddingTop: 0,
          color: 'inherit',
          paddingBottom: 0,
          '& .MuiListItemButton-root': {
            transition: COLLAPSE_ANIMATION,
            height: 40,
            marginLeft: -1,
            marginRight: -1
          }
        }}
        subheader={
          <ListSubheader disableSticky sx={{ px: 1.25, my: -1 }}>
            {item.label}
          </ListSubheader>
        }
      >
        {children?.map((child) => (
          <NavMenuItem
            key={child.label}
            item={child}
            baseStyle
            disableTooltip
            iconless={allChildrenWithoutIcons}
          />
        ))}
      </ListContainer>
    }
  >
    <Tooltip
      title={!disableTooltip && !isNavMenuExpanded && !children ? item.label : ''}
      placement="right"
      TransitionComponent={Zoom}
      arrow
      componentsProps={{ tooltip: { style: { marginLeft: 6, fontSize: 11 } } }}
    >
      <ListItemButton
        selected={isSelected()}
        sx={{
          pl: 2,
          pr: 2,
          color: 'inherit',
          '&& .Mui-selected, && .Mui-selected:hover': {
            '.MuiTypography-root': {
              fontWeight: (theme) =>
                isNavMenuExpanded
                  ? theme.typography.fontWeightBold
                  : theme.typography.fontWeightRegular
            }
          }
        }}
      >
        {iconless ? (
          <div
            style={{
              width: isNavMenuExpanded ? ICONLESS_EXPANDED_WIDTH : ICONLESS_COLLAPSED_WIDTH
            }}
          />
        ) : (
          <ListItemIcon>{item.icon && <item.icon />}</ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          sx={{
            ml: -2.5,
            opacity: !baseStyle && !isNavMenuExpanded ? '0' : '1',
            transition: COLLAPSE_ANIMATION
          }}
        />
        {children !== undefined && expanded && (
          <ExpandLess
            onClick={toggleExpanded}
            sx={{
              color: isSelected() ? 'primary.main' : 'inherit',
              opacity: isNavMenuExpanded ? '1' : '0',
              transition: COLLAPSE_ANIMATION
            }}
          />
        )}
        {children !== undefined && !expanded && (
          <ExpandMore
            onClick={toggleExpanded}
            sx={{
              color: isSelected() ? 'primary.main' : 'inherit',
              opacity: isNavMenuExpanded ? '1' : '0',
              transition: COLLAPSE_ANIMATION
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  </RichTooltip>
)
